import * as React from 'react'
import { connect } from 'react-redux'

import { Hint, RadialChart } from 'react-vis'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import AccidentSummaryCard from './AccidentSummaryCard.component'

import { RootState } from '../../../store'
import { AccidentType } from 'src/lib/interfaces/AccidentType.interface'
import { CountSummary } from 'src/lib/interfaces/CountSummary.interface'
import { AccidentSummary as AccidentSummaryInterface } from 'src/lib/interfaces/AccidentSummary'

import * as accidentsSelectors from '../../../store/accidents/selectors'

const get = require('lodash/get')
const map = require('lodash/map')
const slice = require('lodash/slice')
const isEmpty = require('lodash/isEmpty')
const takeWhile = require('lodash/takeWhile')

const CHART_COLORS = [ '#17a2b8', '#f6b425', '#b2bec3', '#fd79a8', '#00b894', '#2d3436', '#6c5ce7', '#ffeaa7' ]

export interface AccidentSummaryProps {
  isLoading?: boolean
  data?: AccidentSummaryInterface
  pendingStatusData?: CountSummary
  claimTypeById: (id: number) => AccidentType|undefined
}

interface AccidentSummaryState {
  hoveredItem?: any
}

class AccidentSummary extends React.PureComponent<AccidentSummaryProps, AccidentSummaryState> {
  state: AccidentSummaryState = {
    hoveredItem: null
  }

  get pieChartData() {
    const { data, claimTypeById } = this.props

    if (data && !isEmpty(data.countByType)) {
      const countTotal = data.current.count!
      let countSum = 0
      let stoppedAtIndex = 0

      const ultil70Percent = takeWhile(data.countByType, (countByType, index) => {
        if (!countSum) {
          countSum += countByType.count
          return true
        }

        if (countSum / countTotal <= 0.75) {
          countSum += countByType.count
          return true
        }

        countSum += countByType.count
        stoppedAtIndex = index
        return false
      })

      const pieChartData = map(ultil70Percent, (countByType, index) => {
        const claimType = claimTypeById(parseInt(countByType.id, 10))
        const label = get(claimType, 'name')
        return {
          label,
          angle: countByType.count,
          color: CHART_COLORS[index],
          hintLabel: [{ title: label, value: countByType.count }]
        }
      })

      if (data.countByType.length > stoppedAtIndex) {
        const others = slice(data.countByType, stoppedAtIndex)

        let othersCount = 0
        const othersLabels = map(others, countByType => {
          othersCount += countByType.count
          const claimType = claimTypeById(parseInt(countByType.id, 10))
          return { value: countByType.count, title: get(claimType, 'name') }
        })

        pieChartData.push({
          label: 'Others',
          angle: othersCount,
          hintLabel: othersLabels,
          color: CHART_COLORS[stoppedAtIndex]
        })
      }

      return pieChartData
    }

    return null
  }

  render() {
    const { hoveredItem } = this.state
    const { data, isLoading, pendingStatusData } = this.props

    const currentYearClaimsValue = get(data, 'current.count', 0)
    const currentYearDamageValue = get(data, 'current.damage', 0)

    const previousYearClaimsValue = get(data, 'previous.count', 0)
    const previousYearDamageValue = get(data, 'previous.damage', 0)

    const pieChartData = this.pieChartData

    return (
      <div className="row no-gutters">
        <AccidentSummaryCard
          title="Claims"
          isLoading={isLoading}
          currentYearValue={currentYearClaimsValue}
          previousYearValue={previousYearClaimsValue}
        />

        <AccidentSummaryCard
          isCurrency
          title="Damage"
          isLoading={isLoading}
          currentYearValue={currentYearDamageValue}
          previousYearValue={previousYearDamageValue}
        />

        {pendingStatusData && (
          <AccidentSummaryCard
            title="Pending"
            isLoading={isLoading}
            currentYearValue={pendingStatusData.count}
          />
        )}

        {pieChartData &&
          <div
            style={{ height: '150px', overflow: 'visible', minWidth: '120px' }}
            className="ggcard d-flex flex-row align-items-center justify-content-center"
          >
            {isLoading
              ? (
                <h4 className="card-title d-flex align-items-center mb-0">
                  <FontAwesomeIcon spin icon={['fas', 'spinner']} />
                </h4>
              ) : (
                <React.Fragment>
                  <RadialChart
                    width={150}
                    height={150}
                    data={pieChartData}
                    colorType="literal"
                    onMouseLeave={() => this.setState({ hoveredItem: null })}
                    onValueMouseOver={item => this.setState({ hoveredItem: item })}
                  >
                    {hoveredItem &&
                      <Hint
                        getX={i => i.x}
                        getY={i => i.y}
                        value={hoveredItem}
                        format={i => i.hintLabel}
                      />
                    }
                  </RadialChart>

                  <div className="mr-3">
                    {map(pieChartData, (element, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '5px',
                            backgroundColor: CHART_COLORS[index]
                          }}
                        />
                        <span className="ml-2" style={{ fontSize: '12px' }}>{element.label}</span>
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              )
            }
          </div>
        }
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  pendingStatusData: state.accidents.pendingStatusData,
  claimTypeById: accidentsSelectors.getClaimTypeById(state)
})

export default connect(mapStateToProps)(AccidentSummary)



// WEBPACK FOOTER //
// ./src/routes/accidents/components/AccidentSummary.component.tsx